import { useMemo, useState, useEffect, useRef } from "react";
import { Input } from "antd";
import {
  SearchOutlined,
  FileSearchOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import type { BillOut } from "../../../../api/bills";
import { TablesEmptyState } from "./TablesEmptyState";
import { TableCard } from "./TableCard";
import { DesktopTableCard } from "./DesktopTableCard";
import { sortBillsForTableList } from "./tableOrder";
import { useConfiguration } from "../../../../hooks/useConfiguration";
import { createSearchMatcher } from "../../../../utils/search";
import { useTableCardInfo } from "./useTableCardInfo";
import { VoiceOrderButton } from "./voice/VoiceOrderButton";
import { CardResizeGrips } from "../cardResize/CardResizeGrips";
import { CardLayoutPreview } from "../cardResize/CardLayoutPreview";
import {
  TABLES_CARD_RESIZE,
  cardGridClassOf,
  cardGridStyleOf,
  isOneLineCard,
  useTablesLayout,
  useTablesLayoutPreview,
} from "./layout/tablesLayout";

interface TableListProps {
  bills: BillOut[];
  selectedBillId: number | null;
  onTableSelect: (billId: number) => void;
  /** Избор със стрелките (десктоп) — само смяна на селекцията, без
      презареждане на сметките: задържан клавиш иначе спамва GET /bills. */
  onTableKeyboardSelect: (billId: number) => void;
  /** Двоен клик на маса — на телефон скрива релсата с маси (демо UX) */
  onTableDoubleClick?: (billId: number) => void;
  showClosedTables?: boolean;
  /** Масите, чието закриване още чака принтера — кутийката им се върти и не
      се крие, каквато и да е настройката за закритите (`payment/closingBills.ts`). */
  closingBillIds?: ReadonlySet<number>;
  isModalOpen?: boolean;
  /** Фокусът на стрелките е в редовете на избраната маса
      (useOrderItemsKeyboard) — тук стрелките и Esc мълчат. */
  itemsKeyboardActive?: boolean;
  /** → от най-десния край на реда с маси: преминава в редовете на избраната
      маса. Връща false, ако няма къде (празна/обобщена сметка) — тогава →
      продължава към следващата маса, както досега. */
  onArrowRightEdge?: () => boolean;
  /** Гласова поръчка — микрофонът вдясно от търсачката (без shortcut).
      Родителят го подава САМО при право open_bill_voice — без него иконата
      не се рендира изобщо. */
  onVoiceOrder?: () => void;
  /** Локален режим (docs/edge.md): микрофонът стои, но е disabled с подсказка. */
  voiceLocked?: boolean;
}

export const TableList = ({
  bills,
  selectedBillId,
  onTableSelect,
  onTableKeyboardSelect,
  onTableDoubleClick,
  showClosedTables = true,
  closingBillIds,
  isModalOpen = false,
  itemsKeyboardActive = false,
  onArrowRightEdge,
  onVoiceOrder,
  voiceLocked = false,
}: TableListProps) => {
  const { t } = useTranslation();
  const { isNarrow, isDesktop } = useViewport();
  // Сервитьор, клиент, гостите на резервацията… — общото с картите в режим
  // „Схема" (`useTableCardInfo`).
  const { describe, cardPropsOf } = useTableCardInfo();

  const [searchTerm, setSearchTerm] = useState("");
  // Подредбата на масите е настройка на ресторанта (Конфигурация → Маркиране).
  const { orderingTableOrder } = useConfiguration().configuration;
  // DOM елементите на картите — за стрелките (редовете/колоните се четат от
  // реалните позиции, защото гридът е auto-fill) и за скрол до избраната.
  const cardRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Под десктоп прага родителят дава тясна релса (124–176px) → компактни карти.
  const compact = !isDesktop;
  // На десктоп картите са с размера от дръжките на избраната карта
  // (`CardResizeGrips`): картите на ред и височината — за всички.
  const cardColumns = useTablesLayout((layout) => layout.cardColumns);
  const cardHeight = useTablesLayout((layout) => layout.cardHeight);
  const cardLayout = { railWidth: null, cardColumns, cardHeight };
  const oneLine = !compact && isOneLineCard(cardHeight);
  // Влачи се дръжка на карта — новият размер като сиви контури отгоре.
  const preview = useTablesLayoutPreview();

  const sortedBills = useMemo(
    () =>
      sortBillsForTableList(
        bills,
        showClosedTables,
        orderingTableOrder,
        closingBillIds,
      ),
    [bills, showClosedTables, orderingTableOrder, closingBillIds],
  );

  // Обогатяване на масите с derived полета (сервитьор, клиент, гостите на
  // настанената резервация) — за филтрацията и картите.
  const enrichedBills = useMemo(
    () => sortedBills.map(describe),
    [sortedBills, describe],
  );

  // Търсенето по име на маса, сервитьор и гостите на настанената резервация е
  // толерантно към „шльокавица" (`utils/search.ts`) — „Иван" излиза и на „ivan";
  // сумата си остава обикновено съвпадение по подниз (числата нямат транслитерация).
  const filteredBills = useMemo(() => {
    const matcher = createSearchMatcher(searchTerm);
    if (matcher.isEmpty) return enrichedBills;
    const term = searchTerm.trim().toLowerCase();
    return enrichedBills.filter(
      ({ bill, waiterLabel, reservationGuestName, total }) => {
        const nameMatch = matcher.matches(bill.name);
        const totalMatch = total.toFixed(2).includes(term);
        const waiterMatch = waiterLabel ? matcher.matches(waiterLabel) : false;
        const guestMatch = reservationGuestName
          ? matcher.matches(reservationGuestName)
          : false;
        return nameMatch || totalMatch || waiterMatch || guestMatch;
      },
    );
  }, [enrichedBills, searchTerm]);

  // Глобална клавиатурна търсачка на десктоп — без нужда от клик върху input.
  useEffect(() => {
    if (!isDesktop || isModalOpen) return;

    // Стрелки по картите (десктоп): ← → са предишна/следваща маса в реда на
    // списъка; ↑ ↓ отиват в най-близкия ред отгоре/отдолу, в най-близката
    // колона — по реалните DOM позиции, защото колоните зависят от ширината.
    // → от последната карта в реда минава в редовете на масата вдясно.
    const moveSelectionByArrow = (key: string) => {
      const visibleBills = filteredBills.map(({ bill }) => bill);
      if (visibleBills.length === 0) return;
      const currentIndex = visibleBills.findIndex(
        (bill) => bill.id === selectedBillId,
      );
      let nextIndex = currentIndex === -1 ? 0 : currentIndex;
      if (currentIndex !== -1) {
        if (key === "ArrowLeft") nextIndex = Math.max(0, currentIndex - 1);
        if (key === "ArrowRight") {
          const currentTop = cardRefs.current
            .get(visibleBills[currentIndex].id)
            ?.getBoundingClientRect().top;
          const nextTop = visibleBills[currentIndex + 1]
            ? cardRefs.current
                .get(visibleBills[currentIndex + 1].id)
                ?.getBoundingClientRect().top
            : undefined;
          const isRowEnd =
            currentTop !== undefined &&
            (nextTop === undefined || Math.abs(nextTop - currentTop) >= 5);
          if (isRowEnd && onArrowRightEdge?.()) return;
          nextIndex = Math.min(visibleBills.length - 1, currentIndex + 1);
        }
        if (key === "ArrowUp" || key === "ArrowDown") {
          const positions = visibleBills.flatMap((bill, index) => {
            const element = cardRefs.current.get(bill.id);
            if (!element) return [];
            const rect = element.getBoundingClientRect();
            return [{ index, top: rect.top, left: rect.left }];
          });
          const current = positions.find((p) => p.index === currentIndex);
          if (!current) return;
          const candidates = positions.filter((p) =>
            key === "ArrowUp"
              ? p.top < current.top - 5
              : p.top > current.top + 5,
          );
          if (candidates.length === 0) return;
          // Най-близкият ред (по top), в него — най-близката колона (по left)
          const nearestTop = candidates.reduce((best, p) =>
            Math.abs(p.top - current.top) < Math.abs(best.top - current.top)
              ? p
              : best,
          ).top;
          const inRow = candidates.filter(
            (p) => Math.abs(p.top - nearestTop) < 5,
          );
          nextIndex = inRow.reduce((best, p) =>
            Math.abs(p.left - current.left) < Math.abs(best.left - current.left)
              ? p
              : best,
          ).index;
        }
      }
      const nextBill = visibleBills[nextIndex];
      if (!nextBill || nextBill.id === selectedBillId) return;
      onTableKeyboardSelect(nextBill.id);
      cardRefs.current
        .get(nextBill.id)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA")
      )
        return;
      const key = event.key;
      const isArrow = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
      ].includes(key);
      // Стрелките и Esc принадлежат на редовете, докато фокусът е там
      if (itemsKeyboardActive && (isArrow || key === "Escape")) return;
      if (isArrow) {
        event.preventDefault();
        moveSelectionByArrow(key);
        return;
      }
      if (key === "Backspace") {
        event.preventDefault();
        setSearchTerm((prev) => prev.slice(0, -1));
        return;
      }
      if (key === "Escape") {
        // С текст в търсачката Esc я чисти (и е консумиран — preventDefault);
        // празна търсачка → НЕ пипаме събитието, за да стигне до глобалната
        // Esc-навигация „назад" (SharedAccountGate → useEscBack).
        if (searchTerm) {
          event.preventDefault();
          setSearchTerm("");
        }
        return;
      }
      if (/^[a-zA-Zа-яА-Я0-9 .,]$/.test(key)) {
        event.preventDefault();
        setSearchTerm((prev) => prev + key);
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [
    isDesktop,
    isModalOpen,
    searchTerm,
    filteredBills,
    selectedBillId,
    onTableKeyboardSelect,
    itemsKeyboardActive,
    onArrowRightEdge,
  ]);

  return (
    <div className="h-full flex flex-col text-pos-ink">
      {/* Търсачка (компактна) + микрофон за гласова поръчка вдясно от нея;
          мини вариант на тясна релса */}
      <div className={`border-b border-pos-line2 ${compact ? "p-2" : "p-3.5"}`}>
        <div className={`flex items-center ${isNarrow ? "gap-1.5" : "gap-2"}`}>
          <div
            className={`min-w-0 flex-1 flex items-center bg-pos-card border border-pos-line ${
              isNarrow
                ? "gap-1.5 rounded-lg h-8 px-2"
                : "gap-2.5 rounded-xl h-10 px-3"
            }`}
          >
            <SearchOutlined
              className={`text-pos-muted ${isNarrow ? "text-sm" : "text-base"}`}
            />
            <Input
              placeholder={isNarrow ? undefined : t("ordering.searchTable")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              variant="borderless"
              className="!p-0 !text-sm !bg-transparent !text-pos-ink"
            />
          </div>

          {/* Гласова поръчка — икона-микрофон, отваря VoiceOrderModal */}
          {onVoiceOrder && (
            <VoiceOrderButton
              onClick={onVoiceOrder}
              locked={voiceLocked}
              small={isNarrow}
            />
          )}
        </div>
      </div>

      {/* Скролът работи, но лентата е скрита: scrollbar-width за
          Firefox/новите Chromium, ::-webkit-scrollbar за Safari */}
      <div className="flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {filteredBills.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            {searchTerm ? (
              <TablesEmptyState
                icon={<FileSearchOutlined />}
                title={t("ordering.noTablesFound")}
                description={t("ordering.noTablesFoundHint")}
              />
            ) : (
              <TablesEmptyState
                icon={<AppstoreOutlined />}
                title={t("ordering.noOpenTables")}
                description={t("ordering.noOpenTablesHint")}
              />
            )}
          </div>
        ) : (
          <div
            className={
              compact
                ? "grid grid-cols-1 gap-[3px] p-2 content-start"
                : `relative ${cardGridClassOf(cardLayout)}`
            }
            style={compact ? undefined : cardGridStyleOf(cardLayout)}
          >
            {filteredBills.map((info) => {
              const { bill } = info;
              const isSelected = selectedBillId === bill.id;
              const cardProps = {
                cardRef: (element: HTMLButtonElement | null) => {
                  if (element) cardRefs.current.set(bill.id, element);
                  else cardRefs.current.delete(bill.id);
                },
                ...cardPropsOf(info, closingBillIds),
                isSelected,
                searchTerm,
                onClick: () => onTableSelect(bill.id),
                onDoubleClick: onTableDoubleClick
                  ? () => onTableDoubleClick(bill.id)
                  : undefined,
              };
              // Десктоп: клетката е контейнерът, по чиято ширина картата
              // сменя кегела (`FIT` в DesktopTableCard) и подрежда фактите за
              // сметката (`useTableCardFit`); на избраната — дръжките за
              // размера. Докато се влачи — скрити (но на място) под
              // предварителния изглед в новия размер.
              return compact ? (
                <TableCard key={bill.id} {...cardProps} compact />
              ) : (
                <div
                  key={bill.id}
                  className={`@container/card relative flex min-w-0 ${preview ? "opacity-0" : ""}`}
                >
                  <DesktopTableCard {...cardProps} oneLine={oneLine} />
                  {isSelected && <CardResizeGrips spec={TABLES_CARD_RESIZE} />}
                </div>
              );
            })}
            {preview && !compact && (
              <CardLayoutPreview
                spec={TABLES_CARD_RESIZE}
                gridClassName={cardGridClassOf(preview)}
                gridStyle={cardGridStyleOf(preview)}
                cards={filteredBills.map((info) => ({
                  key: info.bill.id,
                  card: (
                    <DesktopTableCard
                      cardRef={() => {}}
                      {...cardPropsOf(info, closingBillIds)}
                      isSelected={false}
                      searchTerm={searchTerm}
                      oneLine={isOneLineCard(preview.cardHeight)}
                      onClick={() => {}}
                    />
                  ),
                }))}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
